// Sprint 2026-Jun — VoltLedger integration Phase E
// Debtor Management Dashboard — a management dashboard, not a reporting screen.
//
// Path: /debtor-dashboard   (module key: debtor_dashboard)
// Shows where management attention is required: overdue, due this week/month,
// high-value overdue, retention pending + approved-awaiting-release, aging
// distribution, top debtors, branch/project/client outstanding, trends.
// All figures pass the SAME reconciliation gate as the Aging Report; any
// mismatch shows a warning chip, never silent inconsistencies.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/ui-helpers";
import { SearchableSelect } from "@/components/SearchableSelect";
import { Card } from "@/components/ui/card";
import {
  AlertTriangle, ArrowRight, CheckCircle2, ChevronLeft, ChevronRight,
  RotateCw, X,
} from "lucide-react";
import {
  Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer,
  Tooltip, XAxis, YAxis,
} from "recharts";

const inr = (v) => (Number(v) || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
const inr2 = (v) => (Number(v) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const lakh = (v) => {
  const n = Number(v) || 0;
  if (Math.abs(n) >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
  if (Math.abs(n) >= 1e5) return `₹${(n / 1e5).toFixed(2)} L`;
  return `₹${inr(n)}`;
};
const selCls = "w-full border border-slate-300 px-2.5 py-2 text-xs bg-white focus:outline-none focus:border-orange-500";
const EMPTY = { fy: "", branch_id: "", client_id: "", project_id: "", project_type: "" };

export default function DebtorDashboard() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState(EMPTY);
  const [data, setData] = useState(null);
  const [retDue, setRetDue] = useState(null);
  const [hr, setHr] = useState(null);
  const [loading, setLoading] = useState(false);
  const [masters, setMasters] = useState({ branches: [], clients: [], projects: [], fys: [] });
  const [groupTab, setGroupTab] = useState("client");
  const [groupSearch, setGroupSearch] = useState("");
  const [iq, setIq] = useState(null);
  const [pc, setPc] = useState(null);
  const [iqSearch, setIqSearch] = useState("");
  const [drill, setDrill] = useState(null); // {title, params}
  const [allowed, setAllowed] = useState(null); // null = all sections
  const show = (key) => !allowed || allowed.includes(key);

  useEffect(() => {
    (async () => {
      try {
        const [b, c, p, f] = await Promise.all([
          api.get("/branches"), api.get("/clients"), api.get("/projects-picker"), api.get("/financial-years"),
        ]);
        const arr = (r) => (Array.isArray(r.data) ? r.data : r.data?.items || []);
        setMasters({ branches: arr(b), clients: arr(c), projects: arr(p), fys: arr(f) });
      } catch { /* optional */ }
    })();
    run(EMPTY);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const params = (f) => ({
    ...(f.fy && { fy: f.fy }), ...(f.branch_id && { branch_id: f.branch_id }),
    ...(f.client_id && { client_id: f.client_id }), ...(f.project_id && { project_id: f.project_id }),
    ...(f.project_type && { project_type: f.project_type }),
  });

  async function run(f = filters) {
    setLoading(true);
    try {
      const hrFY = f.fy ? (/^\d{2}-\d{2}$/.test(f.fy) ? `20${f.fy}` : f.fy) : "";
      const s = await api.get("/debtor-dashboard/summary", { params: params(f) });
      // Per-user section restriction (admin-managed) — restricted sections
      // are stripped server-side; skip fetching their widget endpoints.
      const allowedList = s.data?.debtor_sections_allowed ?? null;
      const ok = (key) => !allowedList || allowedList.includes(key);
      setAllowed(allowedList);
      setData(s.data);
      const [rd, h, q, pcr] = await Promise.all([
        ok("retention_due") ? api.get("/debtor-dashboard/retention-due", { params: { ...params(f), limit: 10 } }).catch(() => null) : null,
        ok("hr_compliance") ? api.get("/hr-compliance/snapshot", { params: hrFY ? { fy: hrFY } : {} }).catch(() => null) : null,
        ok("iq_summary") ? api.get("/debtor-dashboard/invoice-queue-summary", { params: params(f) }).catch(() => null) : null,
        ok("pc_outstanding") ? api.get("/debtor-dashboard/project-client-outstanding", { params: params(f) }).catch(() => null) : null,
      ]);
      setRetDue(rd?.data || null);
      setHr(h?.data || null);
      setIq(q?.data || null);
      setPc(pcr?.data || null);
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load dashboard");
      return false;
    } finally { setLoading(false); }
  }

  const k = data?.kpis || {};
  const recon = data?.reconciliation;
  const set = (key, v) => setFilters((f) => ({ ...f, [key]: v }));
  const openDrill = (title, extra) => setDrill({ title, params: { ...params(filters), ...extra } });

  const groups = ({ client: data?.by_client || [], project: data?.by_project || [], branch: data?.by_branch || [] }[groupTab])
    .filter((g) => !groupSearch || (g.name || "").toLowerCase().includes(groupSearch.toLowerCase()));

  // Project Type options come from the Project Master (`work_type` field).
  const projectTypes = Array.from(new Set(
    masters.projects.map((p) => (p.work_type || "").trim()).filter(Boolean)
  )).sort((a, b) => a.localeCompare(b));
  // A chosen type narrows the Project dropdown to matching projects.
  const projectOptions = filters.project_type
    ? masters.projects.filter((p) => (p.work_type || "").trim() === filters.project_type)
    : masters.projects;

  return (
    <div data-testid="debtor-dashboard-page">
      <PageHeader
        eyebrow="Overview"
        title="Debtor/HR Management Dashboard"
        subtitle="Where management attention is required — overdue receivables, upcoming dues, retention follow-ups, debtor concentration and HR compliance status. Figures reconcile with the Aging Outstanding Report and debtor ledger."
        actions={
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={async () => { if (await run()) toast.success("Dashboard refreshed"); }}
              disabled={loading}
              data-testid="dash-refresh"
              title="Reload all dashboard figures with the current filters"
              className="flex items-center gap-1.5 bg-[#2563EB] hover:bg-[#1D4ED8] disabled:opacity-50 disabled:cursor-not-allowed text-white px-3 py-2 text-xs uppercase tracking-wider font-bold"
            >
              <RotateCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            {recon && (recon.ok ? (
              <span data-testid="dash-recon-ok" className="flex items-center gap-1.5 border border-emerald-300 bg-emerald-50 text-emerald-800 px-3 py-2 text-xs font-bold">
                <CheckCircle2 size={13} /> Reconciled
              </span>
            ) : (
              <a href="/settings/data-tools" data-testid="dash-recon-warning"
                className="flex items-center gap-1.5 border border-amber-300 bg-amber-50 text-amber-800 px-3 py-2 text-xs font-bold hover:bg-amber-100">
                <AlertTriangle size={13} /> {recon.warnings.length} reconciliation warning(s) →
              </a>
            ))}
          </div>
        }
      />

      {/* Filters */}
      <Card className="mb-4 p-3">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 items-end">
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Financial Year</div>
            <select data-testid="dash-fy" value={filters.fy} onChange={(e) => set("fy", e.target.value)} className={selCls}>
              <option value="">All Years</option>
              {masters.fys.map((f) => <option key={f.code || f.id} value={f.code}>{f.code}</option>)}
            </select>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Branch</div>
            <select data-testid="dash-branch" value={filters.branch_id} onChange={(e) => set("branch_id", e.target.value)} className={selCls}>
              <option value="">All Branches</option>
              {masters.branches.map((b) => <option key={b.id} value={b.id}>{b.branch_name || b.name}</option>)}
            </select>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Client</div>
            <select data-testid="dash-client" value={filters.client_id} onChange={(e) => set("client_id", e.target.value)} className={selCls}>
              <option value="">All Clients</option>
              {masters.clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Project Type</div>
            <select data-testid="dash-project-type" value={filters.project_type}
              onChange={(e) => setFilters((f) => ({ ...f, project_type: e.target.value, project_id: "" }))}
              className={selCls}>
              <option value="">All Types</option>
              {projectTypes.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Project</div>
            <SearchableSelect value={filters.project_id}
              onChange={(id) => set("project_id", id)}
              items={projectOptions}
              getLabel={(p) => `${p.project_code ? `${p.project_code} · ` : ""}${p.project_name || p.name || "(unnamed)"}`}
              getSearchText={(p) => `${p.project_code || ""} ${p.project_name || p.name || ""} ${p.client_name || ""}`}
              placeholder="All Projects" searchPlaceholder="Search project code / name…"
              inputClassName="rounded-lg px-3 py-2 text-sm border-slate-300"
              testid="dash-project" />
          </div>
          <div className="flex gap-2">
            <button data-testid="dash-apply" onClick={() => run()}
              className="flex-1 bg-[#F97316] hover:bg-[#EA580C] text-white px-3 py-2 text-xs uppercase tracking-wider font-bold flex items-center justify-center gap-1.5">
              <RotateCw size={12} className={loading ? "animate-spin" : ""} /> Apply
            </button>
            <button onClick={() => { setFilters(EMPTY); run(EMPTY); }}
              className="px-3 py-2 border border-slate-300 text-xs uppercase tracking-wider font-bold text-slate-600 hover:bg-slate-50">Reset</button>
          </div>
        </div>
      </Card>

      {allowed !== null && (
        <div data-testid="debtor-dash-restricted-note" className="mb-3 border border-amber-300 bg-amber-50 text-amber-800 px-3 py-2 text-xs font-bold">
          Some dashboard sections are hidden by your administrator.
        </div>
      )}

      {/* KPI row */}
      {show("kpi_row") && (
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 mb-3">
        <Kpi tid="kpi-outstanding" label="Total Outstanding" value={lakh(k.outstanding)} sub={`${k.open_invoice_count || 0} open invoices`}
          tone="border-slate-300 bg-slate-900 text-white" onClick={() => openDrill("All Outstanding Invoices", { window: "all" })} />
        <Kpi tid="kpi-net-collectible" label="Net Collectible" value={lakh(k.net_collectible)} sub="excl. held retention"
          tone="border-sky-200 bg-sky-50 text-sky-900" onClick={() => openDrill("All Outstanding Invoices", { window: "all" })} />
        <Kpi tid="kpi-collected" label="Amount Collected" value={lakh(k.collected)} sub={`of ${lakh(k.billed)} billed`} tone="border-emerald-200 bg-emerald-50 text-emerald-900" />
        <Kpi tid="kpi-collection-pct" label="Collection %" value={`${k.collection_pct ?? 0}%`} sub="collected ÷ billed" tone="border-emerald-200 bg-emerald-50 text-emerald-900" />
        <Kpi tid="kpi-retention-pending" label="Retention Held" value={lakh(k.retention_pending)} sub="pending with clients"
          tone="border-amber-200 bg-amber-50 text-amber-900" href="/receivable-retentions" />
        <Kpi tid="kpi-retention-awaiting" label="Retention Approved" value={lakh(k.retention_awaiting_release)} sub={`${k.retention_awaiting_release_count || 0} awaiting release`}
          tone="border-sky-200 bg-sky-50 text-sky-900" href="/receivable-retentions" />
        <Kpi tid="kpi-overdue" label="Overdue" value={lakh(k.overdue_amount)} sub={`${k.overdue_count || 0} invoices`}
          tone="border-rose-300 bg-rose-50 text-rose-900" onClick={() => openDrill("Overdue Invoices", { window: "overdue" })} />
      </div>
      )}

      {/* Attention strip */}
      {show("attention_strip") && (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-4">
        <div data-testid="kpi-due-week"
          className="border border-orange-300 bg-orange-50 px-4 py-3 text-left">
          <button onClick={() => openDrill("Collections Due This Week", { window: "due_week" })}
            data-testid="kpi-due-week-open" className="block w-full text-left hover:opacity-80">
            <div className="text-[10px] uppercase tracking-wider font-bold text-orange-700">Collections Due This Week</div>
            <div className="text-lg font-bold text-orange-900">{lakh(k.due_this_week)} <span className="text-xs font-medium">({k.due_this_week_count || 0} inv)</span></div>
          </button>
          <div className="grid grid-cols-3 gap-1.5 mt-2 pt-2 border-t border-orange-200">
            {[["Today", k.due_today, k.due_today_count, "due_today", "Collections Due Today"],
              ["Tomorrow", k.due_tomorrow, k.due_tomorrow_count, "due_tomorrow", "Collections Due Tomorrow"],
              ["Day After", k.due_day_after, k.due_day_after_count, "due_day_after", "Collections Due Day After Tomorrow"],
            ].map(([lbl, amt, cnt, win, title]) => (
              <button key={win} data-testid={`kpi-${win.replace(/_/g, "-")}`}
                onClick={() => openDrill(title, { window: win })}
                className="bg-white/70 border border-orange-200 px-2 py-1.5 text-left hover:bg-white">
                <div className="text-[9px] uppercase tracking-wider font-bold text-orange-600">{lbl}</div>
                <div className="text-xs font-bold text-orange-900 tabular-nums">{lakh(amt)}</div>
                <div className="text-[9px] text-orange-700">{cnt || 0} inv</div>
              </button>
            ))}
          </div>
        </div>
        <button data-testid="kpi-due-month" onClick={() => openDrill("Collections Due This Month", { window: "due_month" })}
          className="border border-sky-300 bg-sky-50 px-4 py-3 text-left hover:bg-sky-100">
          <div className="text-[10px] uppercase tracking-wider font-bold text-sky-700">Collections Due This Month</div>
          <div className="text-lg font-bold text-sky-900">{lakh(k.due_this_month)} <span className="text-xs font-medium">({k.due_this_month_count || 0} inv)</span></div>
        </button>
        <div className="border border-rose-300 bg-white px-4 py-3" data-testid="high-value-overdue">
          <div className="text-[10px] uppercase tracking-wider font-bold text-rose-700 mb-1">High-Value Overdue (Top 5)</div>
          {(data?.high_value_overdue || []).slice(0, 5).map((r) => (
            <div key={r.id} className="flex justify-between text-xs py-0.5 border-b border-slate-50 last:border-0">
              <span className="truncate mr-2">{r.invoice_number} · {r.party_client_name}</span>
              <span className="font-bold text-rose-700 whitespace-nowrap">{lakh(r.outstanding)} · {r.age_days}d</span>
            </div>
          ))}
          {!(data?.high_value_overdue || []).length && <div className="text-xs text-slate-400">None 🎉</div>}
        </div>
      </div>
      )}

      {/* Real-time invoice status tiles — same status engine as the Invoice
          Queue (invoice_status.status_rollup); tiles drill into the queue. */}
      {show("status_tiles") && (
      <div className="border border-slate-200 bg-white px-4 py-3 mb-4" data-testid="status-tiles-section">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Invoice Status — Live (click to open Invoice Queue)</div>
          <div className="flex flex-wrap gap-1.5">
            <button data-testid="status-drill-ses-pending" onClick={() => navigate("/invoice-queue?view=ses_pending")}
              className="border border-amber-300 bg-amber-50 text-amber-800 px-2 py-1 text-[10px] font-bold hover:bg-amber-100">
              SES Pending: {k.ses_pending_count || 0} · {lakh(k.ses_pending_amount)}
            </button>
            <button data-testid="status-drill-due-today" onClick={() => navigate("/invoice-queue?view=due_today")}
              className="border border-sky-300 bg-sky-50 text-sky-800 px-2 py-1 text-[10px] font-bold hover:bg-sky-100">Due Today</button>
            <button data-testid="status-drill-overdue" onClick={() => navigate("/invoice-queue?view=overdue")}
              className="border border-rose-300 bg-rose-50 text-rose-800 px-2 py-1 text-[10px] font-bold hover:bg-rose-100">Overdue</button>
            <button data-testid="status-drill-retention" onClick={() => navigate("/invoice-queue?view=retention_pending")}
              className="border border-amber-300 bg-amber-50 text-amber-800 px-2 py-1 text-[10px] font-bold hover:bg-amber-100">Retention Pending</button>
            <button data-testid="status-drill-fully-paid" onClick={() => navigate("/invoice-queue?view=fully_paid")}
              className="border border-emerald-300 bg-emerald-50 text-emerald-800 px-2 py-1 text-[10px] font-bold hover:bg-emerald-100">Fully Paid</button>
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {(data?.status_tiles || []).map((t) => (
            <button key={t.status} data-testid={`status-tile-${t.status.replace(/[^a-zA-Z0-9]+/g, "-")}`}
              onClick={() => navigate(t.status === "Not Set" ? "/invoice-queue?view=all" : `/invoice-queue?status=${encodeURIComponent(t.status)}`)}
              className={`border px-2.5 py-1.5 text-left hover:opacity-80 ${t.status === "Not Set"
                ? "border-slate-200 bg-slate-50 text-slate-500"
                : "border-sky-200 bg-sky-50 text-sky-900"}`}>
              <div className="text-[10px] font-bold">{t.status}</div>
              <div className="text-[10px] opacity-70">{t.count} inv · {lakh(t.outstanding)} o/s</div>
            </button>
          ))}
          {!(data?.status_tiles || []).length && <div className="text-xs text-slate-400">No invoices.</div>}
        </div>
      </div>
      )}

      {/* Aging buckets */}
      {show("aging_buckets") && (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 mb-4">
        {(data?.aging_buckets || []).map((b, i) => (
          <button key={b.key} data-testid={`dash-bucket-${b.key}`}
            onClick={() => openDrill(`Aging — ${b.label}`, { bucket: b.key })}
            className={`border px-3 py-2.5 text-left hover:opacity-80 ${["border-emerald-200 bg-emerald-50 text-emerald-900", "border-sky-200 bg-sky-50 text-sky-900", "border-amber-200 bg-amber-50 text-amber-900", "border-orange-200 bg-orange-50 text-orange-900", "border-rose-300 bg-rose-100 text-rose-900", "border-slate-300 bg-slate-100 text-slate-900"][i % 6]}`}>
            <div className="text-[10px] uppercase tracking-wider font-bold opacity-70">{b.label}</div>
            <div className="text-sm font-bold">{lakh(b.amount)}</div>
            <div className="text-[10px] opacity-70">{b.count} invoices</div>
          </button>
        ))}
      </div>
      )}

      {/* Charts */}
      {show("charts") && (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <Card className="p-4">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">Monthly Billing vs Collection</div>
          <div style={{ height: 240 }} data-testid="chart-collections">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data?.monthly_trend || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                <YAxis tickFormatter={(v) => lakh(v)} tick={{ fontSize: 10 }} width={70} />
                <Tooltip formatter={(v) => `₹${inr2(v)}`} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="billed" name="Billed" fill="#0f172a" />
                <Bar dataKey="collected" name="Collected" fill="#10b981" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">Outstanding Trend (Cumulative)</div>
          <div style={{ height: 240 }} data-testid="chart-outstanding">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data?.monthly_trend || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                <YAxis tickFormatter={(v) => lakh(v)} tick={{ fontSize: 10 }} width={70} />
                <Tooltip formatter={(v) => `₹${inr2(v)}`} />
                <Line type="monotone" dataKey="outstanding" name="Outstanding" stroke="#f97316" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>
      )}

      {/* Top debtors + group tables */}
      {show("debtor_tables") && (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <Card>
          <div className="px-4 py-3 border-b border-slate-100 text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Top 10 Debtors</div>
          <table className="w-full text-xs" data-testid="top-debtors-table">
            <thead>
              <tr className="bg-slate-100 text-slate-600">
                {["Client", "Outstanding", "Overdue", "Retention", "Inv"].map((h) => (
                  <th key={h} className="px-3 py-2 text-left text-[10px] uppercase tracking-wider">{h}</th>))}
              </tr>
            </thead>
            <tbody>
              {(data?.top_debtors || []).map((g) => (
                <tr key={g.id} className="border-b border-slate-50 hover:bg-orange-50/40 cursor-pointer"
                  onClick={() => openDrill(`Invoices — ${g.name}`, { group: "client", group_id: g.id })}>
                  <td className="px-3 py-2 font-semibold">{g.name}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-bold">{lakh(g.outstanding)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-rose-600">{lakh(g.overdue)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-amber-700">{lakh(g.retention_pending)}</td>
                  <td className="px-3 py-2 text-right">{g.invoice_count}</td>
                </tr>
              ))}
              {!(data?.top_debtors || []).length && <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-400">No outstanding.</td></tr>}
            </tbody>
          </table>
        </Card>
        <Card>
          <div className="px-4 py-2 border-b border-slate-100 flex items-center gap-1 flex-wrap">
            {[["client", "Client-wise"], ["project", "Project-wise"], ["branch", "Branch-wise"]].map(([kx, l]) => (
              <button key={kx} data-testid={`group-tab-${kx}`} onClick={() => setGroupTab(kx)}
                className={`px-3 py-2 text-[10px] uppercase tracking-wider font-bold border-b-2 ${groupTab === kx ? "border-orange-500 text-orange-600" : "border-transparent text-slate-400 hover:text-slate-600"}`}>{l}</button>
            ))}
            <input data-testid="group-search" value={groupSearch} onChange={(e) => setGroupSearch(e.target.value)}
              placeholder={`Search ${groupTab}…`}
              className="ml-auto w-44 border border-slate-300 px-2.5 py-1.5 text-xs bg-white focus:outline-none focus:border-orange-500" />
          </div>
          <div className="max-h-72 overflow-auto">
            <table className="w-full text-xs" data-testid="group-outstanding-table">
              <thead className="sticky top-0">
                <tr className="bg-slate-100 text-slate-600">
                  {["Name", "Outstanding", "Overdue", "Inv"].map((h) => (
                    <th key={h} className="px-3 py-2 text-left text-[10px] uppercase tracking-wider">{h}</th>))}
                </tr>
              </thead>
              <tbody>
                {groups.map((g) => (
                  <tr key={g.id} className="border-b border-slate-50 hover:bg-orange-50/40 cursor-pointer"
                    onClick={() => openDrill(`Invoices — ${g.name}`, { group: groupTab, group_id: g.id })}>
                    <td className="px-3 py-2 font-semibold">{g.name}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-bold">{lakh(g.outstanding)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-rose-600">{lakh(g.overdue)}</td>
                    <td className="px-3 py-2 text-right">{g.invoice_count}</td>
                  </tr>
                ))}
                {!groups.length && <tr><td colSpan={4} className="px-3 py-6 text-center text-slate-400">{groupSearch ? "No match for your search." : "No outstanding."}</td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
      )}

      {/* Invoice Queue summary — client / project / status, count & amount */}
      {show("iq_summary") && (
      <Card className="mb-4" data-testid="iq-summary-widget">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Invoice Queue — Count &amp; Amount</div>
            <div className="text-xs text-slate-400 mt-0.5" data-testid="iq-summary-totals">
              {iq ? `${iq.totals.invoices} invoices · ${lakh(iq.totals.outstanding)} outstanding` : "Loading…"} — click a row to open the Invoice Queue
            </div>
          </div>
          <button onClick={() => navigate("/invoice-queue?view=all")} data-testid="iq-summary-open-link"
            className="text-xs font-bold text-orange-600 hover:text-orange-700 flex items-center gap-1">
            Open Invoice Queue <ArrowRight size={12} />
          </button>
        </div>
        <div className="px-4 py-2 border-b border-slate-100 flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Client → Project → Status</span>
          <input data-testid="iq-summary-search" value={iqSearch} onChange={(e) => setIqSearch(e.target.value)}
            placeholder="Search client / project / status…"
            className="ml-auto w-64 border border-slate-300 px-2.5 py-1.5 text-xs bg-white focus:outline-none focus:border-orange-500" />
        </div>
        <div className="max-h-96 overflow-auto">
          <table className="w-full text-xs" data-testid="iq-summary-table">
            <thead className="sticky top-0">
              <tr className="bg-slate-100 text-slate-600">
                {["Client", "Project", "Status", "Count", "Amount"].map((h) => (
                  <th key={h} className={`px-3 py-2 text-[10px] uppercase tracking-wider ${h === "Count" || h === "Amount" ? "text-right" : "text-left"}`}>{h}</th>))}
              </tr>
            </thead>
            <tbody>
              {(() => {
                const rows = (iq?.matrix || []).filter((m) => !iqSearch ||
                  [m.client_name, m.project_name, m.status].some((t) => (t || "").toLowerCase().includes(iqSearch.toLowerCase())));
                let prevClient = null, prevProject = null;
                return rows.map((m, i) => {
                  const showClient = m.client_id !== prevClient;
                  const showProject = showClient || m.project_id !== prevProject;
                  prevClient = m.client_id; prevProject = m.project_id;
                  return (
                    <tr key={`${m.client_id}|${m.project_id}|${m.status}`} data-testid={`iq-summary-row-${i}`}
                      className={`border-b border-slate-50 hover:bg-orange-50/40 cursor-pointer ${showClient ? "border-t border-slate-200" : ""}`}
                      onClick={() => {
                        const qs = new URLSearchParams();
                        if (m.client_id !== "—") qs.set("client_id", m.client_id);
                        if (m.project_id !== "—") qs.set("project_id", m.project_id);
                        if (m.status !== "Not Set") qs.set("status", m.status);
                        navigate(`/invoice-queue?${qs.toString() || "view=all"}`);
                      }}>
                      <td className="px-3 py-2 font-semibold max-w-[220px] truncate" title={m.client_name}>
                        {showClient ? m.client_name : <span className="text-slate-300">〃</span>}
                      </td>
                      <td className="px-3 py-2 max-w-[220px] truncate" title={m.project_name}>
                        {showProject ? m.project_name : <span className="text-slate-300">〃</span>}
                      </td>
                      <td className="px-3 py-2">
                        <span className={`border px-1.5 py-0.5 text-[10px] font-bold whitespace-nowrap ${m.status === "Not Set" ? "border-slate-200 bg-slate-50 text-slate-500" : "border-sky-200 bg-sky-50 text-sky-800"}`}>{m.status}</span>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{m.count}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-bold">{lakh(m.outstanding)}</td>
                    </tr>
                  );
                });
              })()}
              {!(iq?.matrix || []).length && (
                <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-400">No invoices in the queue.</td></tr>)}
            </tbody>
          </table>
        </div>
      </Card>
      )}

      {/* Retention Due for Release — READ ONLY */}
      {show("retention_due") && (
      <Card className="mb-4" data-testid="retention-due-widget">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Retention Due for Release (Approved, Awaiting Client)</div>
            <div className="text-xs text-slate-400 mt-0.5">Read-only follow-up list — total {lakh(retDue?.grand_total)} across {retDue?.total || 0} request(s)</div>
          </div>
          <a href="/receivable-retentions" className="text-xs font-bold text-orange-600 hover:text-orange-700 flex items-center gap-1">
            Open Retentions <ArrowRight size={12} />
          </a>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3">
          <div className="lg:col-span-2 overflow-x-auto">
            <table className="w-full text-xs" data-testid="retention-due-table">
              <thead>
                <tr className="bg-slate-100 text-slate-600">
                  {["Client", "Project", "Branch", "Invoice", "Head", "Amount", "Approved On", "Expected Release", "Days Pending", "Status"].map((h) => (
                    <th key={h} className="px-2.5 py-2 text-left text-[10px] uppercase tracking-wider whitespace-nowrap">{h}</th>))}
                </tr>
              </thead>
              <tbody>
                {(retDue?.rows || []).map((r) => (
                  <tr key={r.id} className="border-b border-slate-50">
                    <td className="px-2.5 py-1.5 whitespace-nowrap font-semibold">{r.client_name}</td>
                    <td className="px-2.5 py-1.5 whitespace-nowrap max-w-[140px] truncate" title={r.project_name}>{r.project_name}</td>
                    <td className="px-2.5 py-1.5 whitespace-nowrap">{r.branch_name || "—"}</td>
                    <td className="px-2.5 py-1.5 whitespace-nowrap">{r.invoice_number}</td>
                    <td className="px-2.5 py-1.5 whitespace-nowrap">{r.head}</td>
                    <td className="px-2.5 py-1.5 text-right tabular-nums font-bold">₹{inr2(r.amount)}</td>
                    <td className="px-2.5 py-1.5 whitespace-nowrap">{r.approval_date || "—"}</td>
                    <td className="px-2.5 py-1.5 whitespace-nowrap">{r.expected_release_date || "—"}</td>
                    <td className="px-2.5 py-1.5 text-right">{r.days_pending ?? "—"}</td>
                    <td className="px-2.5 py-1.5"><span className="border border-sky-200 bg-sky-50 text-sky-700 px-1.5 py-0.5 text-[10px] font-bold uppercase">{r.status}</span></td>
                  </tr>
                ))}
                {!(retDue?.rows || []).length && <tr><td colSpan={10} className="px-3 py-6 text-center text-slate-400">No approved retention awaiting release.</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="border-l border-slate-100 p-4">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">Total Pending by Client</div>
            {(retDue?.totals_by_client || []).slice(0, 8).map((g) => (
              <div key={g.client_name} className="flex justify-between text-xs py-1 border-b border-slate-50 last:border-0">
                <span className="truncate mr-2">{g.client_name} <span className="text-slate-400">({g.count})</span></span>
                <span className="font-bold whitespace-nowrap">₹{inr2(g.total)}</span>
              </div>
            ))}
            {!(retDue?.totals_by_client || []).length && <div className="text-xs text-slate-400">—</div>}
          </div>
        </div>
      </Card>
      )}

      {show("pc_outstanding") && pc && <ProjectClientOutstandingWidget pc={pc} openDrill={openDrill} />}

      {show("hr_compliance") && hr && <HRComplianceWidget hr={hr} />}

      {drill && <DashDrillModal drill={drill} onClose={() => setDrill(null)} />}
    </div>
  );
}

const PC_RET_COLS = [
  ["ret_po", "Retention PO Terms"], ["ret_ir", "IR Retention"],
  ["ret_safety", "Safety Retention"], ["ret_bg", "BG Retention"],
  ["ret_gst", "GST Retention"], ["ret_other", "Other Retention"],
];

function ProjectClientOutstandingWidget({ pc, openDrill }) {
  const [q, setQ] = useState("");
  const groups = (pc.groups || []).filter((g) => !q ||
    `${g.project_name} ${g.client_name}`.toLowerCase().includes(q.toLowerCase()));
  const t = pc.totals || {};
  const ret = (v) => (Number(v) > 0.004
    ? <span className="text-indigo-700 font-bold">₹{inr2(v)}</span>
    : <span className="text-slate-300">—</span>);
  const numCls = "px-2.5 py-2 text-right tabular-nums whitespace-nowrap";
  return (
    <Card className="mb-4" data-testid="pc-outstanding-widget">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Project · Client Outstanding — Retention Heads</div>
          <div className="text-xs text-slate-400 mt-0.5">
            One row per project + client pair over open invoices — click a row to drill into its invoices
          </div>
        </div>
        <input data-testid="pc-outstanding-search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search project / client…"
          className="w-56 border border-slate-300 px-2.5 py-1.5 text-xs bg-white focus:outline-none focus:border-orange-500" />
      </div>
      <div className="max-h-[28rem] overflow-auto">
        <table className="w-full text-xs" data-testid="pc-outstanding-table">
          <thead className="sticky top-0 z-10">
            <tr className="bg-slate-100 text-slate-600">
              {["Project", "Client", "Inv", "Outstanding", "TDS", "CESS", "LD",
                ...PC_RET_COLS.map(([, l]) => l), "Pend. Retn."].map((h, i) => (
                <th key={h} className={`px-2.5 py-2 text-[10px] uppercase tracking-wider whitespace-nowrap bg-slate-100 ${i < 2 ? "text-left" : "text-right"}`}>{h}</th>))}
            </tr>
          </thead>
          <tbody>
            <tr className="border-b-2 border-indigo-200 bg-indigo-50/70 font-bold text-indigo-900" data-testid="pc-outstanding-totals-row">
              <td className="px-2.5 py-2 uppercase tracking-wider text-[10px]" colSpan={2}>All Groups</td>
              <td className={numCls}>{t.invoices || 0}</td>
              <td className={`${numCls} text-orange-700`}>₹{inr2(t.outstanding)}</td>
              <td className={numCls}>₹{inr2(t.tds)}</td>
              <td className={numCls}>₹{inr2(t.cess)}</td>
              <td className={numCls}>₹{inr2(t.ld)}</td>
              {PC_RET_COLS.map(([k]) => <td key={k} className={numCls}>{ret(t[k])}</td>)}
              <td className={`${numCls} text-indigo-800`}>₹{inr2(t.pending_retention)}</td>
            </tr>
            {groups.map((g) => (
              <tr key={`${g.project_id}|${g.client_id}`} data-testid={`pc-outstanding-row-${g.project_id}-${g.client_id}`}
                className="border-b border-slate-50 hover:bg-orange-50/40 cursor-pointer"
                onClick={() => openDrill(`Invoices — ${g.project_name} · ${g.client_name}`, {
                  ...(g.client_id !== "—" && { client_id: g.client_id }),
                  ...(g.project_id !== "—" && { project_id: g.project_id }),
                })}>
                <td className="px-2.5 py-2 font-semibold max-w-[160px] truncate" title={g.project_name}>{g.project_name}</td>
                <td className="px-2.5 py-2 font-bold max-w-[200px] truncate whitespace-nowrap" title={g.client_name}>
                  {g.client_name} <ArrowRight size={10} className="inline text-slate-400" />
                </td>
                <td className={numCls}>{g.invoices}</td>
                <td className={`${numCls} font-bold text-orange-700`}>₹{inr2(g.outstanding)}</td>
                <td className={numCls}>₹{inr2(g.tds)}</td>
                <td className={numCls}>₹{inr2(g.cess)}</td>
                <td className={numCls}>₹{inr2(g.ld)}</td>
                {PC_RET_COLS.map(([k]) => <td key={k} className={numCls}>{ret(g[k])}</td>)}
                <td className={`${numCls} font-bold text-indigo-800`}>{ret(g.pending_retention)}</td>
              </tr>
            ))}
            {!groups.length && (
              <tr><td colSpan={14} className="px-3 py-6 text-center text-slate-400">
                {q ? "No match for your search." : "No outstanding invoices."}</td></tr>)}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

const HR_ST = {
  Approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Applied: "bg-amber-50 text-amber-700 border-amber-200",
  "No Work": "bg-rose-50 text-rose-700 border-rose-200",
  Pending: "bg-slate-50 text-slate-500 border-slate-200",
};
const HR_CELL = {
  Approved: "bg-emerald-400", Applied: "bg-amber-400",
  "No Work": "bg-rose-300", Pending: "bg-slate-200",
};

function HRComplianceWidget({ hr }) {
  const k = hr.kpis || {};
  const rows = hr.rows || [];
  return (
    <Card className="mb-4" data-testid="hr-compliance-widget">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">HR Compliance Status — FY {hr.fy}</div>
          <div className="text-xs text-slate-400 mt-0.5">
            Rate Contract compliance (incl. manually added RCs) · current month: <span className="font-bold text-slate-600">{hr.month_label}</span>
          </div>
        </div>
        <a href="/hr-compliance" data-testid="hr-widget-open-link"
          className="text-xs font-bold text-orange-600 hover:text-orange-700 flex items-center gap-1">
          Open HR Compliance <ArrowRight size={12} />
        </a>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 p-3 border-b border-slate-100">
        <div className="border border-slate-300 bg-slate-900 text-white px-3 py-2" data-testid="hr-widget-kpi-active">
          <div className="text-[10px] uppercase tracking-wider font-bold opacity-70">Active RCs</div>
          <div className="text-base font-bold">{k.active_rcs || 0}</div>
        </div>
        <div className="border border-emerald-200 bg-emerald-50 text-emerald-900 px-3 py-2" data-testid="hr-widget-kpi-approval-rate">
          <div className="text-[10px] uppercase tracking-wider font-bold opacity-70">Approval Rate</div>
          <div className="text-base font-bold">{k.approval_rate ?? 0}%</div>
          <div className="text-[10px] opacity-70">{k.approved_months || 0} of {k.months_total || 0} months</div>
        </div>
        <div className="border border-rose-300 bg-rose-50 text-rose-900 px-3 py-2" data-testid="hr-widget-kpi-not-applied">
          <div className="text-[10px] uppercase tracking-wider font-bold opacity-70">{hr.month_label} — Not Applied</div>
          <div className="text-base font-bold">{k.month_not_applied || 0} RCs</div>
        </div>
        <div className="border border-amber-200 bg-amber-50 text-amber-900 px-3 py-2" data-testid="hr-widget-kpi-awaiting">
          <div className="text-[10px] uppercase tracking-wider font-bold opacity-70">{hr.month_label} — Awaiting Approval</div>
          <div className="text-base font-bold">{k.month_awaiting_approval || 0} RCs</div>
        </div>
        <div className="border border-sky-200 bg-sky-50 text-sky-900 px-3 py-2" data-testid="hr-widget-kpi-id-cards">
          <div className="text-[10px] uppercase tracking-wider font-bold opacity-70">ID Cards Applied / Approved</div>
          <div className="text-base font-bold">{k.id_cards_applied || 0} / {k.id_cards_approved || 0}</div>
        </div>
        <div className={`border px-3 py-2 ${k.id_card_gap > 0 ? "border-amber-300 bg-amber-50 text-amber-900" : "border-emerald-200 bg-emerald-50 text-emerald-900"}`} data-testid="hr-widget-kpi-id-gap">
          <div className="text-[10px] uppercase tracking-wider font-bold opacity-70">ID Card Gap</div>
          <div className="text-base font-bold">{k.id_card_gap || 0}</div>
        </div>
      </div>
      <div className="overflow-x-auto overflow-y-auto max-h-[420px]">
        <table className="w-full text-xs" data-testid="hr-widget-table">
          <thead className="sticky top-0 z-10">
            <tr className="bg-slate-100 text-slate-600">
              {["RC No", "Project / Site", "Description", `${hr.month_label} Status`, "FY Approved", "Applied", "No Work", "Apr–Mar Trend"].map((h) => (
                <th key={h} className="px-2.5 py-2 text-left text-[10px] uppercase tracking-wider whitespace-nowrap">{h}</th>))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} className="border-b border-slate-50" data-testid={`hr-widget-row-${r.rc_no}`}>
                <td className="px-2.5 py-1.5 whitespace-nowrap font-semibold">
                  {r.rc_no || "—"}
                  {r.is_manual && <span className="ml-1.5 px-1 py-0.5 text-[9px] font-bold uppercase bg-violet-50 text-violet-700 border border-violet-200">Manual</span>}
                </td>
                <td className="px-2.5 py-1.5 whitespace-nowrap max-w-[180px] truncate" title={r.project_name}>{r.project_name}</td>
                <td className="px-2.5 py-1.5 whitespace-nowrap max-w-[140px] truncate text-slate-500" title={r.description}>{r.description || "—"}</td>
                <td className="px-2.5 py-1.5">
                  <span className={`border px-1.5 py-0.5 text-[10px] font-bold uppercase ${HR_ST[r.current_month_status] || HR_ST.Pending}`}>
                    {r.current_month_status}
                  </span>
                </td>
                <td className="px-2.5 py-1.5 text-center tabular-nums font-bold text-emerald-700">{r.fy_approved}</td>
                <td className="px-2.5 py-1.5 text-center tabular-nums text-amber-700">{r.fy_applied}</td>
                <td className="px-2.5 py-1.5 text-center tabular-nums text-rose-600">{r.fy_no_work}</td>
                <td className="px-2.5 py-1.5">
                  <div className="flex gap-0.5">
                    {(r.trend || []).map((s, i) => (
                      <span key={i} title={`${hr.months[i]}: ${s}`} className={`inline-block w-2.5 h-2.5 ${HR_CELL[s] || HR_CELL.Pending}`} />
                    ))}
                  </div>
                </td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-400">No RCs found for FY {hr.fy}.</td></tr>}
          </tbody>
        </table>
      </div>
      {rows.length > 0 && (
        <div className="px-4 py-2 border-t border-slate-100 text-[11px] text-slate-500" data-testid="hr-widget-rowcount">
          {rows.length} RC{rows.length === 1 ? "" : "s"} — sorted by most attention needed (scroll for more)
        </div>
      )}
    </Card>
  );
}

function Kpi({ tid, label, value, sub, tone, onClick, href }) {
  const inner = (
    <>
      <div className="text-[10px] uppercase tracking-wider font-bold opacity-70">{label}</div>
      <div className="text-base font-bold mt-0.5">{value}</div>
      {sub && <div className="text-[10px] opacity-70">{sub}</div>}
    </>
  );
  const cls = `border px-3 py-2.5 text-left w-full ${tone} ${onClick || href ? "hover:opacity-85 cursor-pointer" : ""}`;
  if (href) return <a data-testid={tid} href={href} className={`block ${cls}`}>{inner}</a>;
  if (onClick) return <button data-testid={tid} onClick={onClick} className={cls}>{inner}</button>;
  return <div data-testid={tid} className={cls}>{inner}</div>;
}

function DashDrillModal({ drill, onClose }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const LIMIT = 25;

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/debtor-dashboard/invoices",
          { params: { ...drill.params, skip: page * LIMIT, limit: LIMIT } });
        setRows(r.data.rows || []);
        setTotal(r.data.total || 0);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load invoices");
      } finally { setLoading(false); }
    })();
  }, [drill, page]); // eslint-disable-line react-hooks/exhaustive-deps

  const pages = Math.max(1, Math.ceil(total / LIMIT));
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="dash-drill-modal" onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-6xl max-h-[85vh] flex flex-col shadow-2xl border border-slate-200">
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-orange-600">Drill-down</div>
            <div className="text-sm font-bold text-slate-800">{drill.title} · {total} invoice(s)</div>
          </div>
          <button data-testid="dash-drill-close" onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="overflow-auto flex-1">
          <table className="w-full text-xs">
            <thead className="sticky top-0">
              <tr className="bg-slate-100 text-slate-600">
                {["Invoice", "Date", "Client", "Project", "Branch", "Net Payable", "Receipts", "CN", "DN",
                  "Ret. Pending", "Outstanding", "Due Date", "Age"].map((h) => (
                  <th key={h} className="px-2.5 py-2 text-left text-[10px] uppercase tracking-wider whitespace-nowrap">{h}</th>))}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={13} className="px-3 py-8 text-center text-slate-400">Loading…</td></tr>}
              {!loading && rows.map((r) => (
                <tr key={r.id} className="border-b border-slate-50 hover:bg-slate-50">
                  <td className="px-2.5 py-1.5 font-semibold whitespace-nowrap">{r.invoice_number}</td>
                  <td className="px-2.5 py-1.5 whitespace-nowrap">{(r.invoice_date || "").slice(0, 10)}</td>
                  <td className="px-2.5 py-1.5 whitespace-nowrap">{r.party_client_name}</td>
                  <td className="px-2.5 py-1.5 whitespace-nowrap max-w-[140px] truncate" title={r.project}>{r.project}</td>
                  <td className="px-2.5 py-1.5 whitespace-nowrap">{r.branch_name}</td>
                  <td className="px-2.5 py-1.5 text-right tabular-nums">{inr2(r.net_payable)}</td>
                  <td className="px-2.5 py-1.5 text-right tabular-nums">{inr2(r.total_paid)}</td>
                  <td className="px-2.5 py-1.5 text-right tabular-nums">{inr2(r.credit_note_total)}</td>
                  <td className="px-2.5 py-1.5 text-right tabular-nums">{inr2(r.debit_note_total)}</td>
                  <td className="px-2.5 py-1.5 text-right tabular-nums">{inr2(r.pending_retention)}</td>
                  <td className="px-2.5 py-1.5 text-right tabular-nums font-bold">{inr2(r.outstanding)}</td>
                  <td className="px-2.5 py-1.5 whitespace-nowrap">{r.payment_due_date || <span className="text-rose-500 font-semibold">Missing</span>}</td>
                  <td className="px-2.5 py-1.5 text-right">{r.age_days ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-5 py-2.5 border-t border-slate-100 flex items-center justify-between text-xs">
          <div className="text-slate-500">Page {page + 1} of {pages}</div>
          <div className="flex gap-1.5">
            <button disabled={page === 0} onClick={() => setPage((p) => p - 1)}
              className="px-2.5 py-1.5 border border-slate-300 disabled:opacity-40 hover:bg-slate-50"><ChevronLeft size={13} /></button>
            <button disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)}
              className="px-2.5 py-1.5 border border-slate-300 disabled:opacity-40 hover:bg-slate-50"><ChevronRight size={13} /></button>
          </div>
        </div>
      </div>
    </div>
  );
}

